'use client';

/**
 * Auth Context Provider
 * Manages authentication state and provides auth methods to components
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  login as jwtLogin,
  register as jwtRegister,
  logout as jwtLogout,
  getCurrentUser,
  loginWithGoogle,
  loginWithMicrosoft,
  handleOAuthCallback,
  AuthUser,
  LoginCredentials,
  PendingVerification,
  RegisterData,
} from './jwt-client';

interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  isAuthenticated: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (data: RegisterData) => Promise<PendingVerification>;
  logout: () => Promise<void>;
  loginWithGoogle: () => void;
  loginWithMicrosoft: () => void;
  refreshUser: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  // Initialize auth state on mount
  useEffect(() => {
    async function loadUser() {
      // Check for OAuth callback — called for its side effect (storing tokens).
      handleOAuthCallback();

      // Load user from API
      const currentUser = await getCurrentUser();
      setUser(currentUser);
      setLoading(false);
    }

    loadUser();
  }, []);

  const login = async (credentials: LoginCredentials) => {
    await jwtLogin(credentials);
    const currentUser = await getCurrentUser();
    setUser(currentUser);
  };

  const register = async (data: RegisterData) => jwtRegister(data);

  const logout = async () => {
    await jwtLogout();
    setUser(null);
  };

  const refreshUser = async () => {
    const currentUser = await getCurrentUser();
    setUser(currentUser);
  };

  const value: AuthContextType = {
    user,
    loading,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    loginWithGoogle,
    loginWithMicrosoft,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
